import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Card,
  DriveFolder,
  useAuth,
  kmsService,
  toast,
  DrivePickerButton,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  config,
} from '@aaas/shared';
import { HardDrive, FolderOpen, RefreshCw, Trash2, FileText } from 'lucide-react';
import GoogleDriveFilesDialog from './GoogleDriveFilesDialog';
import {
  useListDriveIntegrations,
  useCreateDriveIntegration,
  useDeleteDriveIntegration,
  useTriggerDriveSync,
} from '../hooks/useDriveIntegrationQuery';

interface ProcessGoogleDriveSectionProps {
  spaceId: string;
}

export default function ProcessGoogleDriveSection({ spaceId }: ProcessGoogleDriveSectionProps) {
  const { idToken } = useAuth();
  const [connectorId, setConnectorId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteWithDocuments, setDeleteWithDocuments] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [integrationToSync, setIntegrationToSync] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const oauthProcessedRef = useRef<string | null>(null);

  // React Query hooks - now with spaceId
  const {
    data: integrationsResponse,
    isLoading,
    error,
  } = useListDriveIntegrations({
    idToken: idToken || '',
    spaceId,
    enabled: !!idToken && !!spaceId,
  });

  const createMutation = useCreateDriveIntegration();
  const deleteMutation = useDeleteDriveIntegration();
  const syncMutation = useTriggerDriveSync();

  const integrations = integrationsResponse?.data?.items || [];

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'DRIVE_OAUTH_SUCCESS') {
        const { connectorId: connId, accessToken: accToken, spaceId: oauthSpaceId } = event.data;

        // Only handle if it's for this space
        if (oauthSpaceId !== spaceId) {
          return;
        }

        if (oauthProcessedRef.current === connId) {
          return;
        }

        oauthProcessedRef.current = connId;
        setConnectorId(connId);
        setAccessToken(accToken);
        setShowPicker(true);
        setIsConnecting(false);
        toast.success('Google Drive connected! Please select a folder or file.');
      } else if (event.data.type === 'DRIVE_OAUTH_ERROR') {
        setIsConnecting(false);
        toast.error(`OAuth failed: ${event.data.error}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [spaceId]);

  const handleConnectDrive = async () => {
    if (!idToken) {
      toast.error('Please log in to continue');
      return;
    }

    // Reset state from any previous OAuth attempts
    oauthProcessedRef.current = null;
    setConnectorId(null);
    setAccessToken(null);
    setShowPicker(false);

    setIsConnecting(true);
    try {
      const oauthCallbackUrl = `${config.api.kmsUrl}/api/v1/kms/storage/oauth/callback`;
      const response = await kmsService.getDriveOAuthUrl(idToken, spaceId, oauthCallbackUrl);

      const oauthUrl = response.data?.oauth_url;

      if (!oauthUrl) {
        throw new Error('Invalid OAuth URL response from server');
      }

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        oauthUrl,
        'Google Drive OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed);
          setIsConnecting(false);
        }
      }, 1000);
    } catch (error: any) {
      setIsConnecting(false);
      toast.error(error.message || 'Failed to start OAuth flow');
    }
  };

  const handleFolderSelected = async (item: DriveFolder) => {
    if (!idToken || !connectorId) {
      toast.error('Missing authentication or connector');
      return;
    }

    try {
      setShowPicker(false);

      const response = await createMutation.mutateAsync({
        idToken,
        spaceId,
        data: {
          connector_id: connectorId,
          resource_id: item.id,
          resource_name: item.name,
          resource_path: item.path || item.name,
          sync_mode: 'auto',
          sync_interval_minutes: item.isFolder ? 15 : 60,
          sync_enabled: true,
        },
      });

      // Trigger auto-sync for all files
      const integrationId = response?.data?.data?.id || response?.data?.id;

      if (integrationId) {
        autoSyncFiles(integrationId);
      } else {
        console.error('Failed to extract integration ID from response:', response);
      }

      setConnectorId(null);
      setAccessToken(null);
      oauthProcessedRef.current = null;

      const filesCount = response?.data?.data?.files_fetched || 0;
      const fetchError = response?.data?.data?.fetch_error;
      const itemType = item.isFolder ? 'Folder' : 'File';

      if (filesCount > 0) {
        toast.success(
          `${itemType} "${item.name}" connected! ` +
            `${filesCount} file(s) are being synced to your knowledge base.`
        );
      } else if (fetchError) {
        toast.warning(`${itemType} connected, but failed to fetch files: ${fetchError}`);
        toast.info('You can manually sync files from the sync button.');
      } else {
        toast.success(`${itemType} "${item.name}" connected successfully!`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create integration');
    }
  };

  const handlePickerCancelled = () => {
    setShowPicker(false);
    setConnectorId(null);
    setAccessToken(null);
    setIsConnecting(false);
    oauthProcessedRef.current = null;
  };

  const autoSyncFiles = async (integrationId: string) => {
    if (!idToken) return;

    try {
      // 1. Fetch list of synced files from the integration
      const filesResponse = await kmsService.listDriveSyncedFiles(idToken, spaceId, integrationId);

      // Backend returns { data: [...] } format
      const files = filesResponse?.data || [];

      if (files.length === 0) {
        return;
      }

      // 2. Trigger sync for each file (fire and forget)
      // Files will appear in DocumentList as they complete via polling
      files.forEach((file: any) => {
        kmsService
          .syncDriveFileToProcess(idToken, spaceId, integrationId, file.file_id)
          .catch((err) => {
            console.error(`Failed to sync file ${file.file_name}:`, err);
            // Don't show error toast - let polling handle status display
          });
      });
    } catch (error) {
      console.error('Failed to fetch synced files for auto-sync:', error);
      // Silent failure - user can manually sync if needed
    }
  };

  const handleSync = (integrationId: string, folderName: string) => {
    if (!idToken) {
      toast.error('Please log in to continue');
      return;
    }

    // Show confirmation dialog instead of syncing immediately
    setIntegrationToSync({ id: integrationId, name: folderName });
    setSyncDialogOpen(true);
  };

  const confirmSync = async () => {
    if (!idToken || !integrationToSync) {
      return;
    }

    try {
      // Call sync endpoint with deleteExisting=true
      await syncMutation.mutateAsync({
        idToken,
        spaceId,
        integrationId: integrationToSync.id,
        deleteExisting: true,
      });

      toast.success('Sync started! Files will appear as they are processed.');
      setSyncDialogOpen(false);
      setIntegrationToSync(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to trigger sync');
    }
  };

  const handleDisconnect = (integrationId: string, folderName: string) => {
    if (!idToken) {
      toast.error('Please log in to continue');
      return;
    }

    setIntegrationToDelete({ id: integrationId, name: folderName });
    setDeleteDialogOpen(true);
  };

  const confirmDisconnect = async () => {
    if (!idToken || !integrationToDelete) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        idToken,
        spaceId,
        integrationId: integrationToDelete.id,
        deleteDocuments: deleteWithDocuments,
      });

      toast.success(
        deleteWithDocuments
          ? 'Connection and all synced files have been deleted'
          : 'Connection disconnected. Synced files remain in your knowledge base.'
      );
      setDeleteDialogOpen(false);
      setIntegrationToDelete(null);
      setDeleteWithDocuments(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to disconnect');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'paused':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading Google Drive connections...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-red-600 mb-2">
            <svg className="h-8 w-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="font-semibold mb-1">Failed to Load Integrations</h3>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Google Drive</h2>
          <p className="text-sm text-muted-foreground">
            Sync files from Google Drive to this process
          </p>
        </div>
        <Button onClick={handleConnectDrive} disabled={isConnecting} size="sm" className="gap-2">
          <HardDrive className="h-4 w-4" />
          {isConnecting ? 'Connecting...' : 'Connect Folder'}
        </Button>
      </div>

      {/* Integrations List */}
      {integrations.length === 0 ? (
        <Card className="p-8">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <HardDrive className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold">No Google Drive Connected</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Connect a Google Drive folder to sync files to this process
              </p>
            </div>
            <Button
              onClick={handleConnectDrive}
              disabled={isConnecting}
              size="sm"
              className="gap-2"
            >
              <HardDrive className="h-4 w-4" />
              Connect your first folder
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-3">
          {integrations.map((integration: any) => (
            <Card key={integration.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FolderOpen className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{integration.resource_name}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(
                          integration.status
                        )}`}
                      >
                        {integration.status}
                      </span>
                      {!integration.sync_enabled && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-600 bg-gray-50">
                          paused
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div>
                        <span className="text-muted-foreground">Last Sync:</span>{' '}
                        <span className="font-medium">{formatDate(integration.last_sync_at)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Files Tracked:</span>{' '}
                        <span className="font-medium">{integration.total_files_synced}</span>
                      </div>
                    </div>
                    {integration.total_sync_errors > 0 && (
                      <div className="mt-1 text-xs text-red-600">
                        {integration.total_sync_errors} sync errors
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedIntegration({
                        id: integration.id,
                        name: integration.resource_name,
                      });
                      setFilesDialogOpen(true);
                    }}
                    className="gap-2"
                  >
                    <FileText className="h-3 w-3" />
                    View Files
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync(integration.id, integration.resource_name)}
                    disabled={syncMutation.isPending}
                    className="gap-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Sync
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnect(integration.id, integration.resource_name)}
                    disabled={deleteMutation.isPending}
                    className="gap-2 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Google Picker */}
      {showPicker && connectorId && accessToken && (
        <DrivePickerButton
          onFolderSelected={handleFolderSelected}
          onPickerCancelled={handlePickerCancelled}
          accessToken={accessToken}
          autoTrigger={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setDeleteWithDocuments(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Drive Folder?</AlertDialogTitle>
            <AlertDialogDescription>
              What would you like to do with files synced from "{integrationToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="deleteOption"
                checked={!deleteWithDocuments}
                onChange={() => setDeleteWithDocuments(false)}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Keep files in knowledge base</div>
                <div className="text-sm text-muted-foreground">
                  Disconnect the folder but preserve all synced files. New files will not be synced.
                </div>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="deleteOption"
                checked={deleteWithDocuments}
                onChange={() => setDeleteWithDocuments(true)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-red-600">Delete files from knowledge base</div>
                <div className="text-sm text-muted-foreground">
                  Remove the connection AND delete all synced files from your knowledge base
                  permanently.
                </div>
              </div>
            </label>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteWithDocuments(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              className={deleteWithDocuments ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {deleteWithDocuments ? 'Delete Everything' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sync Confirmation Dialog */}
      <AlertDialog
        open={syncDialogOpen}
        onOpenChange={(open) => {
          setSyncDialogOpen(open);
          if (!open) {
            setIntegrationToSync(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-sync "{integrationToSync?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will perform a complete refresh of all files from this folder.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-4 text-sm">
            <div className="font-medium">What will happen:</div>
            <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
              <li>
                All existing documents from this folder will be deleted from your knowledge base
              </li>
              <li>Fresh copies of all files will be downloaded from Google Drive</li>
              <li>New documents will be created and indexed for Q&A</li>
              <li>This ensures your knowledge base is in sync with the latest file versions</li>
            </ul>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-800">
              <strong>Note:</strong> Files will appear in your document list as they are processed.
              This may take a few minutes for large folders.
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSync}>Start Sync</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Google Drive Files Dialog */}
      {filesDialogOpen && selectedIntegration && (
        <GoogleDriveFilesDialog
          isOpen={filesDialogOpen}
          onClose={() => {
            setFilesDialogOpen(false);
            setSelectedIntegration(null);
          }}
          spaceId={spaceId}
          integrationId={selectedIntegration.id}
          integrationName={selectedIntegration.name}
        />
      )}
    </div>
  );
}
